import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';

import { ErrorService } from '../services/error/error.service';
import { LocalStorageService } from '../services/local-storage.service';
import { ChangePasswordService } from './change-password.service';
import { ResetPasswordData, ResetPasswordResponse } from './change-password.domain';
import { ResponseMessageModel } from '../domains/common.domain';
import { SubmitService } from '../services/submit.service';
import { MessageType } from '../constants/app.constants';
import { ActivatedRoute, Router } from '@angular/router';
import { ValidationsService } from '../services/validations.service';

@Component({
  providers: [ChangePasswordService],
  selector: 'app-change-password',
  styleUrls: ['./change-password.component.scss'],
  templateUrl: './change-password.component.html',
})
export class ChangePasswordComponent implements OnInit {
  @Output() changeView: EventEmitter<number>;
  changePasswordForm: FormGroup;
  disableSubmitButton: boolean;
  errorMessage: string;
  successMessage: string;
  userEmail: string;
  isLoading: boolean;
  showPasswordOne: boolean;
  showPasswordTwo: boolean;
  showPasswordThree: boolean;

  constructor(
    private changePasswordService: ChangePasswordService,
    private errorService: ErrorService,
    private localStorageService: LocalStorageService,
    private messageService: MessageService,
    private submitService: SubmitService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.changeView = new EventEmitter<number>();
  }

  ngOnInit(): void {
    this.disableSubmitButton = true;
    this.userEmail = this.localStorageService.getItem('user_email');
    this.prepareForm();
    this.isLoading = false;
    this.showPasswordOne = false;
    this.showPasswordTwo = false;
    this.showPasswordThree = false;
  }

  saveFormData(): void {
    if (this.changePasswordForm.valid) {
      const requestData: ResetPasswordData = {
        registration: [
          {
            email: this.changePasswordForm.value.email,
          },
          {
            password: this.changePasswordForm.value.oldPassword,
          },
          {
            new_password: this.changePasswordForm.value.newPassword,
          },
        ],
      };

      const subscription = this.changePasswordService.updatePassword(requestData);
      this.isLoading = true;
      subscription.subscribe(
        (response: ResetPasswordResponse) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true, this.errorHandler);
          if (response && response.result === 'success') {
            // this.messageService.add({severity:'success', summary:'Success:', detail:'Password successfully Updated'});
            this.successMessage = 'app.ui-messages.success'; // response.result;
            this.createMessageToast(this.successMessage, 'success');
            this.submitService.setMessage(MessageType.PASSWORD_CHANGED);
            this.router.navigate(['/submitted']);
          }
        },
        error => {
          this.isLoading = false;
          if (error.status === 422) {
            this.errorMessage = error.error.message;
          } else {
            this.errorMessage = 'app.ui-messages.error'; // 'something went wrong';
            this.createMessageToast(this.errorMessage, 'error');
            this.submitService.setMessage(MessageType.ERROR);
          }
          // this.messageService.add({severity:'error', summary:'Error', detail:this.errorMessage});
        }
      );
    }
  }

  resetForm(): void {
    this.changePasswordForm.reset();
    this.changePasswordForm.controls.email.setValue(this.userEmail || '');
    this.changePasswordForm.controls.email.setValidators([Validators.required, ValidationsService.emailValidator()]);
    this.disableSubmitButton = true;
  }

  errorHandler = (): void => {
    this.errorMessage = 'something went wrong';
  };

  createMessageToast(message: string, severity: string): void {
    const responseMessage: ResponseMessageModel = {
      severity,
      detail: message,
      data: {
        msgType: 'locale',
      },
    };
    responseMessage.life = 3000;
    responseMessage.key = 'app-notifier';
    if (this.messageService) {
      this.messageService.clear('app-notifier');
      this.messageService.add(responseMessage);
    }
  }

  prepareForm(): void {
    this.changePasswordForm = new FormGroup({
      email: new FormControl(this.userEmail || '', [Validators.required, ValidationsService.emailValidator()]),
      oldPassword: new FormControl('', [Validators.required]),
      newPassword: new FormControl('', [Validators.required]),
      repeatPassword: new FormControl('', [Validators.required]),
    });

    this.changePasswordForm.valueChanges.subscribe(values => {
      this.errorMessage = '';
      this.successMessage = '';
      if (this.changePasswordForm.valid) {
        this.disableSubmitButton = values.newPassword !== values.repeatPassword;
      } else {
        this.disableSubmitButton = true;
      }
    });
  }
}
